import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { EmployeeInfo, PortalModule, UserContext } from '@typeDefs/home/responses/home';
import { ClockAction_Request } from '@typeDefs/employee/requests/employee';
import { ApiDataResponse } from '@shared/model/api-response.model';

@Injectable({
  providedIn: 'root',
})
export class HomeService {
  http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  getBulletins(sectionToLoad: string) {
    return this.http.get('/Employee/GetBulletinsGetIndex?sectionToLoad=' + sectionToLoad).toPromise();
  }

  getLastLogonDate() {
    return this.http.get('/Home/GetLastLogonDate', { responseType: 'text' }).toPromise();
  }

  getModules(): Promise<PortalModule[]> {
    return this.http.get<PortalModule[]>('/Home/GetModules').toPromise();
  }

  getClockInStatus(fromFieldKiosk: boolean, currentTimeString: any) {
    return this.http
      .get('/Employee/GetClockInStatus?fromFieldKiosk=' + fromFieldKiosk + '&currentTimeString=' + currentTimeString + '&where=1')
      .toPromise();
  }

  postClockAction(requestObj: ClockAction_Request) {
    return this.http.post('/Employee/PostClockAction', { clockAction: requestObj }).toPromise();
  }

  getCurrentStatus() {
    return this.http.get('/Performance/GetCurrentStatus').toPromise();
  }

  getJobs(form: string): Promise<Job[]> {
    const url = '/Employee/GetJobs';
    const httpParams = new HttpParams().set('form', form);
    return this.http.get<Job[]>(url, { params: httpParams }).toPromise();
  }

  setJob(jCJMKeyID: any, userKeyID: any) {
    return this.http.post('/DailyReports/SetJob', { lastUsedJCJMKeyID: jCJMKeyID, userKeyID: userKeyID }).toPromise();
  }

  getPhasesByJCJMKeyID(jCJMKeyID: any): Promise<Phase[]> {
    const url = '/Employee/GetPhasesByJCJMKeyID';
    const httpParams = new HttpParams().set('jCJMKeyID', jCJMKeyID).set('laborOnly', true);
    return this.http.get<Phase[]>(url, { params: httpParams }).toPromise();
  }

  getTypeSign(type: string) {
    return this.http.get('/Checklist/GetTypeSign?type=' + type).toPromise();
  }

  setHotlistItem(module) {
    return this.http.post('/Home/SetHotlistItem', { moduleID: module.ModuleID, isItemAdded: module.IsHotlistItem }).toPromise();
  }

  sendAcknowledge(
    bulletinID: any,
    bulletinAttachDocumentFlagValue: any,
    filename: any,
    bulletinSignatureText: any,
    bulletinFileData: any,
    bulletinFileName: any,
    isFillablePDF: any,
    languageID?: number
  ) {
    return this.http
      .post('/Employee/sendAcknowledge', {
        bulletinID: bulletinID,
        bulletinAttachDocumentFlagValue: bulletinAttachDocumentFlagValue,
        filename: filename,
        bulletinSignatureText: bulletinSignatureText,
        bulletinFileData: bulletinFileData,
        bulletinFileName: bulletinFileName,
        isFillablePDF: isFillablePDF,
        languageID: languageID
      })
      .toPromise();
  }

  getBulletinsGetIndex(bulletinID: any, onboardID?: any, languageID?: number) {
    const url = '/Employee/GetBulletinsGetIndex?bulletinID=' + bulletinID + '&onboardID=' + onboardID + "&languageID=" + languageID;
    return this.http.get(url).toPromise();
  }

  getDBAttachmentList(moduleID: number, bulletinID?: any, recordGuid?: any, onboardingUser?: boolean, languageID?: number, defaultToEnglish: boolean = true) {
    const url =
      '/Document/GetDBAttachmentList?moduleID=' +
      moduleID +
      '&recordID=' +
      bulletinID +
      '&recordGuid=' +
      recordGuid +
      '&onboardingUser=' +
      onboardingUser +
      '&languageID=' +
      languageID +
      '&defaultToEnglish=' +
      defaultToEnglish;
    return this.http.get(url).toPromise();
  }

  sentBulletinSign(
    bulletinID: any,
    bulletinAttachDocumentFlagValue: any,
    signatureStr: any,
    bulletinSignatureText: any,
    latitude: any,
    longitude: any,
    filename: any,
    bulletinFileData: any,
    bulletinFileName: any,
    isFillablePDF: any,
    languageID?: number
  ) {
    return this.http
      .post('/Employee/SentBulletinSign', {
        bulletinID: bulletinID,
        bulletinAttachDocumentFlagValue: bulletinAttachDocumentFlagValue,
        signatureStr: signatureStr,
        bulletinSignatureText: bulletinSignatureText,
        latitude: latitude,
        longitude: longitude,
        filename: filename,
        bulletinFileData: bulletinFileData,
        bulletinFileName: bulletinFileName,
        isFillablePDF: isFillablePDF,
        languageID: languageID
      })
      .toPromise();
  }

  getCompanyLogo(co: number, size: string) {
    return this.http.get('/Company/GetLogo?co=' + co + '&size=' + size.toUpperCase(), { responseType: 'text' }).toPromise();
  }

  getPortalName(co: number) {
    return this.http.get('/Company/GetPortalName?co=' + co, { responseType: 'text' }).toPromise();
  }

  getEmployeeInfo(): Promise<EmployeeInfo> {
    return this.http.get<EmployeeInfo>('/Employee/GetInfo').toPromise();
  }

  getUserFields() {
    return this.http.get('/Employee/GetUserFields').toPromise();
  }

  getComboItemsByFieldID(fieldID: any) {
    return this.http.get('/Employee/GetComboItemsByFieldID?fieldID=' + fieldID).toPromise();
  }

  personalInfoUpdate(postData: any) {
    return this.http
      .post('/Employee/PersonalInfoUpdate', {
        formData: postData,
        recordKeyID: null,
      })
      .toPromise();
  }

  setDocConsent(cellPhone: any, email: string, consentPaystubs: boolean, consentW2T4: boolean, consentACA1095: boolean, optOutSMS: any) {
    return this.http
      .post('/Employee/SetDocConsent', {
        cellPhone: cellPhone,
        email: email,
        consentPaystubs: consentPaystubs,
        consentW2T4: consentW2T4,
        consentACA1095: consentACA1095,
        optOutSMS: optOutSMS,
      })
      .toPromise();
  }

  unbindViewpointID() {
    return this.http.post('/Account/UnbindViewpointID', {}).toPromise();
  }

  getUserContext(): Promise<UserContext> {
    return this.http.get<UserContext>('/Account/GetUserContext').toPromise();
  }

  getManagerGrid() {
    return this.http.get('/Performance/GetManagerGrid').toPromise();
  }

  getPdfData(ID: string, flattenPDF = false, disabledRequiredFields = false) {
    return this.http
      .get(
        '/Document/GetRawAttachmentData?attachmentID=' +
          ID +
          '&keepOriginal=false&flattenPDF=' +
          flattenPDF +
          '&disabledRequiredFields=' +
          disabledRequiredFields
      )
      .toPromise();
  }

  getApprovalCounts() {
    return this.http.get('/Home/GetApprovalCounts').toPromise();
  }
}

export interface Job {
  JCJMKeyID?: number;
  Job: string;
  JCCo?: number;
  JobDescription: string;
  JobDisplayName: string;
  DisplayValue: string;
}

export interface Phase {
  JCJPKeyID: number;
  PhaseGroup: number;
  Phase: string;
  PhaseDescription: string;
}
